// components/RecordListCellEditor.tsx
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import SysColumn from "app/class/sys-column";
import { useMemo, useState } from "react";
import { useChoiceOptions } from "modules/record/shared/hooks/useChoiceOptions";
import { ChoiceAutocompleteInput } from "modules/record/shared/components/ChoiceAutocompleteInput";
import { BooleanFieldInput } from "modules/record/shared/fields/BooleanFieldInput";
import { DateFieldInput } from "modules/record/shared/fields/DateFieldInput";
import { DateTimeFieldInput } from "modules/record/shared/fields/DateTimeFieldInput";
import { TimeFieldInput } from "modules/record/shared/fields/TimeFieldInput";
import { NumberFieldInput } from "modules/record/shared/fields/NumberFieldInput";
import { CurrencyFieldInput } from "modules/record/shared/fields/CurrencyFieldInput";
import { TextFieldInput } from "modules/record/shared/fields/TextFieldInput";
import { ColorPickerFieldInput } from "modules/record/shared/fields/ColorPickerFieldInput";
import { useDebounce } from "shared/hooks/useDebounce";
import { useEvaluateReferenceFilter } from "modules/record/record-producer/hooks/useEvaluateReferenceFilter";
import { ReferenceAutocompleteInput } from "modules/record/shared/components/ReferenceAutocompleteInput";
import { TableNameAutocompleteInput } from "modules/record/shared/components/TableNameAutocompleteInput";
import { useReferenceDisplayColumn, useReferenceOptions } from "modules/record/shared/hooks/useReferenceOptions";
import { extractReferenceId, resolveDynamicReferenceTableName } from "modules/record/shared/services/dynamicReference";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";

interface RecordListCellEditorProps {
    column: SysColumn;
    params: GridRenderEditCellParams;
}

const getEditorPreferredWidth = (column: SysColumn) => {
    switch (column.column_type) {
        case "BOOLEAN":
            return 120;
        case "INTEGER":
        case "NUMBER":
        case "DECIMAL":
        case "CURRENCY":
            return 180;
        case "DATE":
        case "DATETIME":
        case "TIME":
            return 240;
        case "COLOR_PICKER":
            return 220;
        case "CHOICE":
        case "REFERENCE":
        case "DYNAMIC_REFERENCE":
        case "TABLE_NAME":
            return 320;
        default:
            return column.column_subtype === "MULTILINE" ? 420 : 280;
    }
};

const RecordListCellEditorComponent = ({ column, params }: RecordListCellEditorProps) => {
    const apiRef = useGridApiContext();
    const [referenceSearch, setReferenceSearch] = useState("");
    const { data: columns = [] } = useColumns();
    const { data: tables = [] } = useTables();
    const debouncedReferenceSearch = useDebounce(referenceSearch, 400);
    const { data: choiceOptions = [], isLoading: choicesLoading } = useChoiceOptions(
        column.column_type === "CHOICE" ? column : undefined
    );
    const { evaluateReferenceFilter } = useEvaluateReferenceFilter();
    const selectedReferenceId = useMemo(() => extractReferenceId(params.value), [params.value]);
    const dynamicReferenceTableName = useMemo(() => {
        if (column.column_type !== "DYNAMIC_REFERENCE") {
            return column.referenced_table_name || "";
        }

        const dependencyColumn = columns.find((candidate) => candidate.id === column.depends_on_column_id);
        const dependencyValue = dependencyColumn?.name
            ? (params.row as Record<string, any>)?.[dependencyColumn.name]
            : null;

        return resolveDynamicReferenceTableName({
            column,
            columns,
            tables,
            dependencyValue,
        });
    }, [column, columns, params.row, tables]);
    const { displayColumnName } = useReferenceDisplayColumn(dynamicReferenceTableName);
    const currencyDependencyValue = useMemo(() => {
        if (!column.depends_on_column_id) {
            return null;
        }

        const dependencyColumn = columns.find((candidate) => candidate.id === column.depends_on_column_id);
        if (!dependencyColumn?.name) {
            return null;
        }

        const row = params.row as Record<string, any>;
        const value = row?.[dependencyColumn.name];
        if (typeof value === "string") {
            return value;
        }

        if (value && typeof value === "object" && "id" in value) {
            return String((value as Record<string, unknown>).id ?? "");
        }

        return null;
    }, [column.depends_on_column_id, columns, params.row]);
    const evaluatedReferenceFilters = useMemo(() => {
        if (!["REFERENCE", "DYNAMIC_REFERENCE"].includes(column.column_type) || !column.reference_filter) {
            return null;
        }

        return evaluateReferenceFilter(column.reference_filter, params.row as Record<string, any>) ?? null;
    }, [column, evaluateReferenceFilter, params.row]);
    const { data: referenceOptions = [], isLoading: referenceLoading } = useReferenceOptions({
        tableName: ["REFERENCE", "DYNAMIC_REFERENCE"].includes(column.column_type) ? dynamicReferenceTableName : undefined,
        selectedId: selectedReferenceId,
        search: debouncedReferenceSearch,
        displayColumnName,
        evaluatedFilters: evaluatedReferenceFilters,
    });

    const handleChange = (value: any) => {
        apiRef.current.setEditCellValue({
            id: params.id,
            field: params.field,
            value,
        });
    };

    const renderEditorShell = (children: React.ReactNode) => (
        <Box
            sx={{
                width: `min(calc(100vw - 96px), max(100%, ${getEditorPreferredWidth(column)}px))`,
                maxWidth: "calc(100vw - 96px)",
                p: 0.75,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                boxShadow: 3,
                boxSizing: "border-box",
                overflow: "hidden",
            }}
        >
            {children}
        </Box>
    );

    switch (column.column_type) {
        case "BOOLEAN":
            return renderEditorShell(
                <BooleanFieldInput
                    value={params.value}
                    onChange={handleChange}
                    variant="checkbox"
                />
            );

        case "DATE":
            return renderEditorShell(
                <DateFieldInput
                    value={params.value ?? null}
                    onChange={handleChange}
                />
            );

        case "DATETIME":
            return renderEditorShell(
                <DateTimeFieldInput
                    value={params.value ?? null}
                    onChange={handleChange}
                />
            );

        case "TIME":
            return renderEditorShell(
                <TimeFieldInput
                    value={params.value ?? null}
                    onChange={handleChange}
                />
            );

        case "INTEGER":
        case "NUMBER":
        case "DECIMAL":
            return renderEditorShell(
                <NumberFieldInput
                    value={params.value ?? ""}
                    onChange={handleChange}
                    autoFocus
                />
            );

        case "CURRENCY":
            return renderEditorShell(
                <CurrencyFieldInput
                    column={column}
                    value={params.value ?? null}
                    onChange={handleChange}
                    currencyCodeOverride={typeof currencyDependencyValue === "string" ? currencyDependencyValue : null}
                    autoFocus
                />
            );

        case "COLOR_PICKER":
            return renderEditorShell(
                <ColorPickerFieldInput
                    value={params.value ?? ""}
                    onChange={handleChange}
                    autoFocus
                />
            );

        case "CHOICE":
            return renderEditorShell(
                <ChoiceAutocompleteInput
                    options={choiceOptions}
                    loading={choicesLoading}
                    value={params.value}
                    onChange={(value) => handleChange(value)}
                    autoFocus
                />
            );

        case "REFERENCE":
        case "DYNAMIC_REFERENCE":
            return renderEditorShell(
                <ReferenceAutocompleteInput
                    options={referenceOptions}
                    loading={referenceLoading}
                    selectedId={selectedReferenceId}
                    displayField={displayColumnName}
                    referenceTable={dynamicReferenceTableName}
                    onChange={handleChange}
                    onSearchChange={setReferenceSearch}
                    autoFocus
                    showOpenButton
                    disabled={column.column_type === "DYNAMIC_REFERENCE" && !dynamicReferenceTableName}
                />
            );

        case "TABLE_NAME":
            return renderEditorShell(
                <TableNameAutocompleteInput
                    value={params.value ?? ""}
                    onChange={handleChange}
                    autoFocus
                />
            );

        default:
            return renderEditorShell(
                <TextFieldInput
                    value={params.value ?? ""}
                    onChange={handleChange}
                    autoFocus
                />
            );
    }
};

export default RecordListCellEditorComponent;
